import React, { useEffect, useState, useRef } from "react";
import Layout from "../../components/Layout";
import { useCompliance } from "../../context/ComplianceContext";
import { useProjects } from "../../context/ProjectContext";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useNavigate, useParams } from "react-router-dom";
// ✅ ADDED: Unsaved changes protection imports
import { useUnsavedChanges } from "../../hooks/useUnsavedChanges";
import NavigationBlocker from "../../components/common/NavigationBlocker";
import { toInputDate } from "../../utils/dateUtils";

const ComplianceForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { saveCompliance, fetchComplianceById, getCompliance } = useCompliance();
  const { projects } = useProjects();
  const loadedIdRef = useRef(null);

  // ✅ ADDED: isDirty state
  const [isDirty, setIsDirty] = useState(false);

  const [formData, setFormData] = useState({
    projectId: "",
    complianceName: "",
    authorityName: "",
    certificateNumber: "",
    issueDate: "",
    expiryDate: "",
    status: "Valid",
    documentUrl: ""
  });

  // Load Data on Edit safely
  useEffect(() => {
    if (!id) return;
    if (loadedIdRef.current === String(id)) return;

    let isMounted = true;
    const loadRecord = async () => {
      try {
        const data = await fetchComplianceById(id);
        if (data && isMounted) {
          loadedIdRef.current = String(id);
          setFormData({
            id: data.id,
            projectId: data.projectId || data.project_id || "",
            complianceName: data.complianceName || data.title || "",
            authorityName: data.authorityName || data.authority || "",
            certificateNumber: data.certificateNumber || data.certificate_no || "",
            issueDate: toInputDate(data.issueDate || data.issue_date),
            expiryDate: toInputDate(data.expiryDate || data.expiry_date),
            status: data.status || "Valid",
            documentUrl: data.documentUrl || data.document_url || ""
          });
        }
      } catch (err) {
        console.error("Failed to load compliance record:", err);
      }
    };
    loadRecord();

    return () => {
      isMounted = false;
    };
  }, [id, fetchComplianceById]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    // ✅ ADDED: Mark form as dirty on any change
    setIsDirty(true);
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    saveCompliance(formData);
    // ✅ ADDED: Reset dirty state on successful save
    setIsDirty(false);
    toast.success(id ? "Record Updated!" : "Record Created Successfully!");
    setTimeout(() => navigate("/admin/compliance-safety"), 1000);
  };

  return (
    <>
      <div className="p-6 bg-white min-h-screen">
        <h1 className="text-base font-bold mb-6 text-gray-800">
            {id ? "Edit Compliance Record" : "New Compliance Record"}
        </h1>

        <form onSubmit={handleSubmit} className="max-w-4xl bg-white shadow-lg rounded-xl p-8 border border-gray-100">
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            
            {/* 1. Project Selection */}
            <div className="md:col-span-2">
                <label className="block text-sm font-bold text-gray-700 mb-1">Link to Project</label>
                <select 
                    name="projectId" 
                    value={formData.projectId} 
                    onChange={handleChange} 
                    className="w-full border border-gray-300 p-2.5 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
                >
                    <option value="">-- General / No Project --</option>
                    {projects.map(p => (
                        <option key={p.id} value={p.id}>{p.name}</option>
                    ))}
                </select>
            </div>

            {/* 2. Compliance Name */}
            <div className="md:col-span-2">
              <label className="block text-sm font-bold text-gray-700 mb-1">Compliance Name</label>
              <input
                type="text"
                name="complianceName"
                value={formData.complianceName}
                onChange={handleChange}
                placeholder="e.g. Fire Safety Clearance"
                required
                className="w-full border border-gray-300 p-2.5 rounded-lg"
              />
            </div>

            {/* 3. Authority & Certificate */}
            <div>
              <label className="block text-sm font-bold text-gray-700 mb-1">Issuing Authority</label>
              <input
                type="text"
                name="authorityName"
                value={formData.authorityName}
                onChange={handleChange}
                placeholder="e.g. Municipal Corp"
                className="w-full border border-gray-300 p-2.5 rounded-lg"
              />
            </div>

            <div>
              <label className="block text-sm font-bold text-gray-700 mb-1">Certificate Number</label>
              <input
                type="text"
                name="certificateNumber"
                value={formData.certificateNumber}
                onChange={handleChange}
                placeholder="e.g. CERT-2024-001"
                className="w-full border border-gray-300 p-2.5 rounded-lg"
              />
            </div>

            {/* 4. Dates */}
            <div>
              <label className="block text-sm font-bold text-gray-700 mb-1">Issue Date</label>
              <input
                type="date"
                name="issueDate"
                value={formData.issueDate}
                onChange={handleChange}
                className="w-full border border-gray-300 p-2.5 rounded-lg"
              />
            </div>

            <div>
              <label className="block text-sm font-bold text-gray-700 mb-1">Expiry Date</label>
              <input
                type="date"
                name="expiryDate"
                value={formData.expiryDate}
                onChange={handleChange}
                className="w-full border border-gray-300 p-2.5 rounded-lg bg-red-50"
              />
            </div>

            {/* 5. Status & Document */}
            <div>
              <label className="block text-sm font-bold text-gray-700 mb-1">Status</label>
              <select
                name="status"
                value={formData.status}
                onChange={handleChange}
                className="w-full border border-gray-300 p-2.5 rounded-lg"
              >
                <option value="Valid">Valid</option>
                <option value="Expired">Expired</option>
                <option value="Renewal Pending">Renewal Pending</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-bold text-gray-700 mb-1">Document Link (URL)</label>
              <input
                type="text"
                name="documentUrl"
                value={formData.documentUrl}
                onChange={handleChange}
                placeholder="https://..."
                className="w-full border border-gray-300 p-2.5 rounded-lg"
              />
            </div>

          </div>

          <div className="mt-8 flex justify-end gap-3">
            <button
                type="button"
                onClick={() => navigate("/admin/compliance-safety")}
                className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 font-bold"
            >
                Cancel
            </button>
            <button
              type="submit"
              className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-bold shadow-md"
            >
              {id ? "Update Record" : "Save Record"}
            </button>
          </div>

        </form>
      </div>

      {/* ✅ ADDED: Unsaved changes protection */}
      {useUnsavedChanges(isDirty)}
      <NavigationBlocker isDirty={isDirty} />
    </>
  );
};

export default ComplianceForm;